import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { ENTRY_PANEL_PAGE_SIZE } from './entryPanelConstants'
import { buildVisiblePages, entryRangeBounds } from './entryPanelPaginationUtils'
import {
  EntryPanelPaginationBar,
  EntryPanelPaginationButton,
  EntryPanelPaginationEllipsis,
  EntryPanelPaginationNav,
  EntryPanelPaginationSummary
} from './interactive.styled'

type Props = {
  page: number
  pageCount: number
  totalCount: number
  pageSize?: number
  onPageChange: (page: number) => void
}

export const EntryPanelPagination: React.FC<Props> = ({
  page,
  pageCount,
  totalCount,
  pageSize = ENTRY_PANEL_PAGE_SIZE,
  onPageChange
}) => {
  const [t] = useTranslation()

  if (totalCount <= 0) {
    return null
  }

  const { from, to } = entryRangeBounds(page, pageSize, totalCount)
  const visiblePages = buildVisiblePages(pageCount, page)
  const showPageControls = pageCount > 1
  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(next, Math.max(pageCount, 1)))
    if (clamped !== page) onPageChange(clamped)
  }

  return (
    <EntryPanelPaginationBar>
      <EntryPanelPaginationSummary aria-live="polite">
        {t('reports.interactive.entryPageRange', { from, to, total: totalCount })}
      </EntryPanelPaginationSummary>

      {showPageControls && (
        <EntryPanelPaginationNav aria-label={t('reports.interactive.entryPagination')}>
          <EntryPanelPaginationButton
            type="button"
            disabled={page <= 1}
            aria-label={t('reports.interactive.entryPagePrev')}
            onClick={() => goToPage(page - 1)}
          >
            <FaChevronLeft aria-hidden />
          </EntryPanelPaginationButton>

          {visiblePages.map((segment, index) =>
            segment === 'ellipsis' ? (
              <EntryPanelPaginationEllipsis key={`ellipsis-${index}`}>
                …
              </EntryPanelPaginationEllipsis>
            ) : (
              <EntryPanelPaginationButton
                key={segment}
                type="button"
                $active={segment === page}
                aria-current={segment === page ? 'page' : undefined}
                onClick={() => goToPage(segment)}
              >
                {segment}
              </EntryPanelPaginationButton>
            )
          )}

          <EntryPanelPaginationButton
            type="button"
            disabled={page >= pageCount}
            aria-label={t('reports.interactive.entryPageNext')}
            onClick={() => goToPage(page + 1)}
          >
            <FaChevronRight aria-hidden />
          </EntryPanelPaginationButton>
        </EntryPanelPaginationNav>
      )}
    </EntryPanelPaginationBar>
  )
}
